import { countries, type Country, type CountryCode } from "@shared/schema";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CountrySelectorProps {
  value: CountryCode;
  onChange: (value: CountryCode) => void;
  className?: string;
}

export function CountrySelector({ value, onChange, className }: CountrySelectorProps) {
  const selectedCountry = countries.find(c => c.code === value);

  return (
    <Select value={value} onValueChange={(v) => onChange(v as CountryCode)}>
      <SelectTrigger 
        className={className} 
        data-testid="select-country"
        aria-label="Select country"
      >
        <SelectValue>
          {selectedCountry && (
            <span className="flex items-center gap-2">
              <span className="text-lg" aria-hidden="true">{selectedCountry.flag}</span>
              <span>{selectedCountry.name}</span>
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {[...countries].sort((a, b) => a.name.localeCompare(b.name)).map((country) => (
          <SelectItem 
            key={country.code} 
            value={country.code}
            data-testid={`option-country-${country.code}`}
          >
            <span className="flex items-center gap-2">
              <span className="text-lg" aria-hidden="true">{country.flag}</span>
              <span>{country.name}</span>
              <span className="text-muted-foreground text-sm">({country.symbol})</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface CountryCardProps {
  country: Country;
  onClick: () => void;
  selected?: boolean;
}

export function CountryCard({ country, onClick, selected }: CountryCardProps) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-3 p-4 rounded-lg border transition-all
        hover-elevate active-elevate-2
        ${selected 
          ? "border-primary bg-primary/5" 
          : "border-border bg-card hover:border-primary/50"
        }
      `}
      data-testid={`card-country-${country.code}`}
    >
      <span className="text-3xl">{country.flag}</span>
      <div className="text-left">
        <p className="font-medium">{country.name}</p>
        <p className="text-sm text-muted-foreground">{country.currency}</p>
      </div>
    </button>
  );
}
